import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ExternalLink, FileText, Pencil, Upload } from "lucide-react";

import { AlertNote } from "../../../components/AlertNote";
import { Button } from "../../../components/Button";
import { CustomTextField } from "../../../components/CustomTextField";
import { MediaInput } from "../../../components/MediaDropZone";
import { UnifiedModal } from "../../../components/UnifiedModal";
import { brand } from "../../../theme/theme";
import { formatFileSize } from "../../../utils/fileSize";
import { DOCUMENT_MAX_MB, isDocumentMedia, isPdfFile } from "./platformMeta";

// Backend `AdaptationImage.title` max_length.
const DOCUMENT_TITLE_MAX = 200;
const TITLE_NOTICE = "Importante: añade un título al documento relacionado con el post, será visible en la publicación.";

/** Local document ({ media_type: "document", document: previewUrl, file, title }), uploaded once the network exists. */
export const toLocalDocument = (file, title) => ({
  id: `local-doc-${Date.now()}-${Math.random()}`,
  media_type: "document",
  document: URL.createObjectURL(file),
  file,
  file_size: file.size,
  title,
});

/** `images` with the title of its document changed. */
export const withDocumentTitle = (images, title) =>
  images.map((item) => (isDocumentMedia(item) ? { ...item, title } : item));

/** Why `file` can't be attached as the document, or "". */
const documentFileError = (file) => {
  if (!isPdfFile(file)) return "Solo se pueden adjuntar documentos PDF.";
  if (file.size > DOCUMENT_MAX_MB * 1024 * 1024) {
    return `El documento pesa ${formatFileSize(file.size)}; el máximo es ${DOCUMENT_MAX_MB} MB.`;
  }
  return "";
};

/**
 * Picks the PDF and its title (mode "add"), or only changes the title (mode "title").
 * `initialFile` is a PDF already dropped on the card. `onSubmit(file, title)` may return a promise;
 * the dialog closes once it resolves.
 */
export function DocumentDialog({ open, mode = "add", initialFile = null, initialTitle = "", onClose, onSubmit }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const adding = mode === "add";

  useEffect(() => {
    if (!open) return;
    const initialError = initialFile ? documentFileError(initialFile) : "";
    setFile(initialFile && !initialError ? initialFile : null);
    setTitle(initialTitle);
    setError(initialError);
    setSaving(false);
  }, [open, initialFile, initialTitle]);

  const pickFile = ([picked] = []) => {
    if (!picked) return;
    const pickError = documentFileError(picked);
    setError(pickError);
    if (!pickError) setFile(picked);
  };

  const submit = async () => {
    const cleanTitle = title.trim();
    if (adding && !file) {
      setError("Elige el PDF que quieres publicar.");
      return;
    }
    if (!cleanTitle) {
      setError("Añade un título al documento: se ve en la publicación.");
      return;
    }
    setSaving(true);
    try {
      await onSubmit(file, cleanTitle);
      onClose();
    } catch {
      // The parent already showed the error; the dialog stays open to retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={() => !saving && onClose()}
      title={adding ? "Adjuntar documento PDF" : "Título del documento"}
      maxWidth="xs"
      footer={
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, width: "100%" }}>
          <Button variant="outline" size="sm" disabled={saving} onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" disabled={saving} onClick={submit}>
            {saving ? "Guardando..." : adding ? "Adjuntar" : "Guardar"}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {adding && (
          <Box>
            <Box
              component="button"
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={saving}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                p: 1.5,
                borderRadius: "12px",
                border: `1px dashed ${file ? brand.softBorder : "#D1D5DB"}`,
                backgroundColor: file ? brand.soft : "#F9FAFB",
                fontFamily: "inherit",
                textAlign: "left",
                cursor: saving ? "default" : "pointer",
                "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 2 },
              }}
            >
              {file ? <FileText size={20} color={brand.pink} /> : <Upload size={20} color="#6B7280" />}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {file ? file.name : "Elegir PDF"}
                </Box>
                <Box sx={{ fontSize: "0.75rem", color: "#6B7280" }}>
                  {file ? formatFileSize(file.size) : `Solo PDF · hasta ${DOCUMENT_MAX_MB} MB`}
                </Box>
              </Box>
              {file && (
                <Box component="span" sx={{ flexShrink: 0, fontSize: "0.75rem", fontWeight: 700, color: brand.pink }}>
                  Cambiar archivo
                </Box>
              )}
            </Box>
            <MediaInput inputRef={inputRef} accept="application/pdf,.pdf" multiple={false} onFiles={pickFile} />
          </Box>
        )}
        <Box>
          <Box component="label" htmlFor="document-title" sx={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#374151", mb: 0.5 }}>
            Título del documento
          </Box>
          <CustomTextField
            id="document-title"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setError("");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                submit();
              }
            }}
            maxLength={DOCUMENT_TITLE_MAX}
            placeholder="Ej.: Guía de SEO local 2026"
            autoFocus={!adding}
          />
        </Box>
        <AlertNote variant="warning" sx={{ p: 1.5 }}>
          {TITLE_NOTICE}
        </AlertNote>
        {error && (
          <Typography role="alert" sx={{ fontSize: "0.8125rem", color: "#DC2626" }}>
            {error}
          </Typography>
        )}
      </Box>
    </UnifiedModal>
  );
}

/** The PDF of a LinkedIn post in the media strip: its title, size, a link to open it and to edit the title. */
export function DocumentFrame({ item, onEditTitle = null, sx = {} }) {
  // Saved PDFs get a random name on the server: only the local file has a name worth showing.
  const name = item.file?.name || "";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: "12px",
        backgroundColor: "#F9FAFB",
        boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.08)",
        ...sx,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 44,
          height: 52,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#FEE2E2",
          color: "#B91C1C",
        }}
      >
        <FileText size={22} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ fontSize: "0.875rem", fontWeight: 700, color: item.title ? "#111827" : "#B91C1C", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.title || "Falta el título del documento"}
        </Box>
        <Box sx={{ fontSize: "0.75rem", color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {["PDF", formatFileSize(item.file_size), name].filter(Boolean).join(" · ")}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 0.5 }}>
          {item.document && (
            <Box
              component="a"
              href={item.document}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.75rem", fontWeight: 700, color: brand.pink, textDecoration: "none" }}
            >
              <ExternalLink size={12} /> Ver PDF
            </Box>
          )}
          {onEditTitle && (
            <LinkButton
              onClick={(event) => {
                event.stopPropagation();
                onEditTitle();
              }}
              sx={{ fontSize: "0.75rem" }}
            >
              <Pencil size={12} /> Editar título
            </LinkButton>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/** Pink text button of the media strip ("Editar título", "Añadir imágenes"…). */
export function LinkButton({ onClick, disabled = false, busy = false, title, sx = {}, children }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      title={title}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        border: "none",
        background: "none",
        p: 0,
        fontFamily: "inherit",
        fontSize: "0.8125rem",
        fontWeight: 700,
        color: brand.pink,
        cursor: busy ? "wait" : disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 2, borderRadius: "4px" },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
